/*
  StatementDocument.jsx
  Hidden A4-width statement template used for the "Statement PDF" download.
  Renders off-screen at fixed 800px width so html2canvas can rasterise a
  properly-branded, ERP-style Statement of Account.

  Props:
    data: snapshotData shape returned by GET /api/collections/projects/{id}/snapshot
    project?: original project object (for contact_* fields not in snapshot)
    absolinLogoUrl: string
*/
import React from "react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const FREQ_LABEL = { one_time: "One Time", monthly: "Monthly", quarterly: "Quarterly", annually: "Annually" };

export default function StatementDocument({ data, project, absolinLogoUrl }) {
  if (!data) return null;
  const projNo = (data.id || project?.id || "").slice(0, 8).toUpperCase();
  const statementNo = `STMT-${projNo}-${(data.as_on || "").replace(/-/g, "").slice(2, 8)}`;
  const currency = (data.currency || project?.currency || "INR").toUpperCase();
  const isUsd = currency === "USD";
  const sym = isUsd ? "$" : "₹";
  const money = (n) => `${sym}${fmt(n)}`;

  const contact = {
    name: data.contact_name || project?.contact_name || "",
    email: data.contact_email || project?.contact_email || "",
    phone: data.contact_phone || project?.contact_phone || "",
  };
  const client = {
    name: data.client_name || project?.client_name || "",
    address: data.client_address || project?.client_address || "",
    state: data.client_state || project?.client_state || "",
    state_code: data.client_state_code || project?.client_state_code || "",
    gstin: data.client_gstin || project?.client_gstin || "",
    phone: data.client_phone || project?.client_phone || "",
    email: data.client_email || project?.client_email || "",
  };

  const lines = data.billing_lines || [];
  const receipts = data.all_receipts || [];
  const totalOutstanding = data.outstanding || 0;

  return (
    <div
      id="statement-document"
      style={{
        position: "fixed",
        top: "-99999px",
        left: "0",
        width: "800px",
        padding: "40px 44px",
        background: "#ffffff",
        color: "#0f172a",
        fontFamily: "'Helvetica Neue', 'Segoe UI', Arial, sans-serif",
        fontSize: "11px",
        lineHeight: 1.4,
        boxSizing: "border-box",
      }}
    >
      {/* ================= HEADER ================= */}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            <td style={{ width: "55%", verticalAlign: "top" }}>
              {absolinLogoUrl && (
                <img src={absolinLogoUrl} alt="Absolin" crossOrigin="anonymous" style={{ height: 42, marginBottom: 8, display: "block" }} />
              )}
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>ABSOLIN SOFTWARE SOLUTIONS LLP</div>
              <div style={{ fontSize: 10, color: "#475569", lineHeight: 1.5, marginTop: 3 }}>
                4th Floor, 50-121-75, SVR Complex, BS Layout,<br />
                Seethammadhara, Visakhapatnam — 530013, Andhra Pradesh, India<br />
                GSTIN: 37ABAFA0565F2ZS · Constitution: LLP<br />
                info@absolin.com
              </div>
            </td>
            <td style={{ width: "45%", verticalAlign: "top", textAlign: "right" }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#0066CC", letterSpacing: "1px" }}>
                STATEMENT
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#64748b", letterSpacing: "2px", marginTop: 2 }}>
                OF ACCOUNT
              </div>
              <table style={{ width: "100%", marginTop: 12, fontSize: 10, color: "#334155" }}>
                <tbody>
                  <tr>
                    <td style={{ textAlign: "right", color: "#94a3b8", padding: "2px 8px 2px 0" }}>Statement No.</td>
                    <td style={{ textAlign: "left", fontWeight: 600, fontFamily: "'Courier New', monospace", width: 130 }}>{statementNo}</td>
                  </tr>
                  <tr>
                    <td style={{ textAlign: "right", color: "#94a3b8", padding: "2px 8px 2px 0" }}>Statement Date</td>
                    <td style={{ textAlign: "left", fontWeight: 600 }}>{fmtDate(data.as_on)}</td>
                  </tr>
                  <tr>
                    <td style={{ textAlign: "right", color: "#94a3b8", padding: "2px 8px 2px 0" }}>Project Ref.</td>
                    <td style={{ textAlign: "left", fontWeight: 600, fontFamily: "'Courier New', monospace" }}>PRJ-{projNo}</td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ borderTop: "2px solid #0066CC", marginTop: 18 }} />

      {/* ================= BILL TO + PROJECT ================= */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 18 }}>
        <tbody>
          <tr>
            <td style={{ width: "50%", verticalAlign: "top", paddingRight: 20 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#64748b", letterSpacing: "1.5px", marginBottom: 6 }}>
                BILL TO
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{client.name || data.name}</div>
              {client.address && (
                <div style={{ marginTop: 3, color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.35 }}>{client.address}</div>
              )}
              {(client.state || client.state_code) && (
                <div style={{ marginTop: 2, color: "#334155" }}>
                  {client.state}{client.state_code ? ` (State Code: ${client.state_code})` : ""}
                </div>
              )}
              {client.gstin && (
                <div style={{ marginTop: 2, color: "#334155" }}>
                  <span style={{ color: "#94a3b8" }}>GSTIN:</span> <strong>{client.gstin}</strong>
                </div>
              )}
              {client.phone && (
                <div style={{ marginTop: 2, color: "#334155" }}>Phone: {client.phone}</div>
              )}
              {client.email && (
                <div style={{ color: "#334155" }}>Email: {client.email}</div>
              )}
              {contact.name && <div style={{ marginTop: 6, color: "#334155" }}>Attn: {contact.name}</div>}
              {contact.email && !client.email && <div style={{ color: "#334155" }}>Email: {contact.email}</div>}
              {contact.phone && !client.phone && <div style={{ color: "#334155" }}>Phone: +{contact.phone}</div>}
            </td>
            <td style={{ width: "50%", verticalAlign: "top", paddingLeft: 20, borderLeft: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#64748b", letterSpacing: "1.5px", marginBottom: 6 }}>
                PROJECT DETAILS
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{data.name}</div>
              <table style={{ marginTop: 4, fontSize: 10, color: "#334155" }}>
                <tbody>
                  <tr>
                    <td style={{ color: "#94a3b8", paddingRight: 10 }}>Type:</td>
                    <td style={{ textTransform: "capitalize" }}>{(data.project_type || "").replace(/_/g, " ")}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "#94a3b8", paddingRight: 10 }}>Status:</td>
                    <td style={{ textTransform: "capitalize", fontWeight: 600 }}>{data.status}</td>
                  </tr>
                  {data.order_date && (
                    <tr>
                      <td style={{ color: "#94a3b8", paddingRight: 10 }}>Order Date:</td>
                      <td>{fmtDate(data.order_date)}</td>
                    </tr>
                  )}
                  {data.start_date && (
                    <tr>
                      <td style={{ color: "#94a3b8", paddingRight: 10 }}>Start Date:</td>
                      <td>{fmtDate(data.start_date)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ================= SUMMARY KPIs ================= */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 22 }}>
        <tbody>
          <tr>
            {[
              { label: "Bill Value / Period", value: `${sym}${fmt((data.contract_one_time || 0) + (data.contract_recurring_per_period || 0))}`, color: "#334155", bg: "#f1f5f9" },
              { label: "Billed to-date", value: `${sym}${fmt(data.expected_billed || data.project_amount)}`, color: "#0066CC", bg: "#eff6ff" },
              { label: "Received", value: `${sym}${fmt(data.total_collected)}`, color: "#059669", bg: "#ecfdf5" },
              { label: "Outstanding", value: `${sym}${fmt(totalOutstanding)}`, color: "#dc2626", bg: "#fef2f2" },
            ].map((k, i) => (
              <td key={i} style={{ padding: "12px 10px", textAlign: "center", background: k.bg, border: "1px solid #e2e8f0", borderRight: i === 3 ? "1px solid #e2e8f0" : "none" }}>
                <div style={{ fontSize: 9, color: "#64748b", fontWeight: 600, letterSpacing: "1px", marginBottom: 4 }}>{k.label.toUpperCase()}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: k.color, fontFamily: "'Helvetica Neue', sans-serif" }}>{k.value}</div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      {/* ================= BILLING SCHEDULE ================= */}
      <div style={{ marginTop: 22 }}>
        <div style={{ fontSize: 11, fontWeight: 700, padding: "8px 10px", background: "#0f172a", color: "#ffffff", letterSpacing: "1px" }}>
          BILLING SCHEDULE
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
          <thead>
            <tr style={{ background: "#f8fafc", color: "#334155" }}>
              <th style={{ padding: 6, textAlign: "center", width: 24, borderBottom: "1px solid #e2e8f0" }}>#</th>
              <th style={{ padding: 6, textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>Bill Line</th>
              <th style={{ padding: 6, textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>Frequency</th>
              <th style={{ padding: 6, textAlign: "right", borderBottom: "1px solid #e2e8f0" }}>Amount / period</th>
              <th style={{ padding: 6, textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>Start</th>
              <th style={{ padding: 6, textAlign: "right", borderBottom: "1px solid #e2e8f0" }}>Periods</th>
              <th style={{ padding: 6, textAlign: "right", borderBottom: "1px solid #e2e8f0" }}>Expected</th>
              <th style={{ padding: 6, textAlign: "right", borderBottom: "1px solid #e2e8f0" }}>Received</th>
              <th style={{ padding: 6, textAlign: "right", borderBottom: "1px solid #e2e8f0" }}>Balance</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: 20, textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>No billing lines configured</td></tr>
            ) : lines.map((ln, i) => (
              <tr key={ln.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: 6, textAlign: "center", color: "#94a3b8" }}>{i + 1}</td>
                <td style={{ padding: 6, fontWeight: 600 }}>{ln.type}</td>
                <td style={{ padding: 6 }}>{FREQ_LABEL[ln.frequency] || ln.frequency}</td>
                <td style={{ padding: 6, textAlign: "right", fontFamily: "'Courier New', monospace" }}>{sym}{fmt(ln.amount)}</td>
                <td style={{ padding: 6 }}>{fmtDate(ln.start_date)}</td>
                <td style={{ padding: 6, textAlign: "right" }}>{ln.completed_periods}</td>
                <td style={{ padding: 6, textAlign: "right", color: "#0066CC", fontWeight: 600, fontFamily: "'Courier New', monospace" }}>{sym}{fmt(ln.expected)}</td>
                <td style={{ padding: 6, textAlign: "right", color: "#059669", fontWeight: 600, fontFamily: "'Courier New', monospace" }}>{sym}{fmt(ln.collected)}</td>
                <td style={{ padding: 6, textAlign: "right", color: ln.outstanding > 0 ? "#dc2626" : "#94a3b8", fontWeight: 700, fontFamily: "'Courier New', monospace" }}>{sym}{fmt(ln.outstanding)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f8fafc", borderTop: "2px solid #cbd5e1", fontWeight: 700 }}>
              <td colSpan={6} style={{ padding: 8, textAlign: "right" }}>TOTALS</td>
              <td style={{ padding: 8, textAlign: "right", color: "#0066CC", fontFamily: "'Courier New', monospace" }}>{sym}{fmt(data.expected_billed)}</td>
              <td style={{ padding: 8, textAlign: "right", color: "#059669", fontFamily: "'Courier New', monospace" }}>{sym}{fmt(data.total_collected)}</td>
              <td style={{ padding: 8, textAlign: "right", color: "#dc2626", fontFamily: "'Courier New', monospace" }}>{sym}{fmt(totalOutstanding)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ================= RECEIPTS ================= */}
      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, padding: "8px 10px", background: "#0f172a", color: "#ffffff", letterSpacing: "1px" }}>
          RECEIPTS &amp; PAYMENTS
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
          <thead>
            <tr style={{ background: "#f8fafc", color: "#334155" }}>
              <th style={{ padding: 6, textAlign: "center", width: 24, borderBottom: "1px solid #e2e8f0" }}>#</th>
              <th style={{ padding: 6, textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>Receipt Date</th>
              <th style={{ padding: 6, textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>Reference / Bank</th>
              <th style={{ padding: 6, textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>Applied to Line</th>
              <th style={{ padding: 6, textAlign: "right", borderBottom: "1px solid #e2e8f0" }}>Amount (INR)</th>
            </tr>
          </thead>
          <tbody>
            {receipts.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 20, textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>No receipts recorded</td></tr>
            ) : [...receipts].sort((a, b) => (a.receipt_date || "").localeCompare(b.receipt_date || "")).map((r, i) => (
              <tr key={r.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: 6, textAlign: "center", color: "#94a3b8" }}>{i + 1}</td>
                <td style={{ padding: 6 }}>{fmtDate(r.receipt_date)}</td>
                <td style={{ padding: 6, color: "#475569", fontFamily: "'Courier New', monospace", fontSize: 9 }}>{r.bank_reference || "—"}</td>
                <td style={{ padding: 6 }}>{r.billing_type ? `${r.billing_type}${r.billing_frequency ? " · " + (FREQ_LABEL[r.billing_frequency] || r.billing_frequency) : ""}` : <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Untagged</span>}</td>
                <td style={{ padding: 6, textAlign: "right", color: "#059669", fontWeight: 700, fontFamily: "'Courier New', monospace" }}>{sym}{fmt(r.collection_amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f8fafc", borderTop: "2px solid #cbd5e1", fontWeight: 700 }}>
              <td colSpan={4} style={{ padding: 8, textAlign: "right" }}>TOTAL RECEIVED</td>
              <td style={{ padding: 8, textAlign: "right", color: "#059669", fontFamily: "'Courier New', monospace" }}>{sym}{fmt(data.total_collected)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ================= PENDING TO CLEAR ================= */}
      <div style={{ marginTop: 24, background: "#fef2f2", border: "2px solid #dc2626", borderRadius: 4, padding: 16 }}>
        <table style={{ width: "100%" }}>
          <tbody>
            <tr>
              <td style={{ width: "60%", verticalAlign: "middle" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#991b1b", letterSpacing: "2px" }}>PENDING AMOUNT TO CLEAR</div>
                <div style={{ fontSize: 10, color: "#7f1d1d", marginTop: 2 }}>As on {fmtDate(data.as_on)} · Kindly remit the amount below to close this statement.</div>
              </td>
              <td style={{ width: "40%", textAlign: "right", verticalAlign: "middle" }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: "#dc2626", fontFamily: "'Helvetica Neue', sans-serif", letterSpacing: "-0.5px" }}>
                  {sym}{fmt(totalOutstanding)}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ================= PAYMENT INFO + SIGNATURE ================= */}
      <table style={{ width: "100%", marginTop: 26, borderCollapse: "collapse", fontSize: 10 }}>
        <tbody>
          <tr>
            <td style={{ width: "60%", verticalAlign: "top" }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#64748b", letterSpacing: "1.5px", marginBottom: 6 }}>
                REMITTANCE DETAILS {isUsd ? "— WIRE TRANSFER (USD)" : ""}
              </div>
              <table style={{ fontSize: 10, color: "#334155" }}>
                <tbody>
                  <tr><td style={{ color: "#94a3b8", paddingRight: 12 }}>Beneficiary:</td><td style={{ fontWeight: 600 }}>Absolin Software Solutions LLP</td></tr>
                  <tr><td style={{ color: "#94a3b8", paddingRight: 12 }}>Bank Name:</td><td>HDFC Bank Ltd.</td></tr>
                  <tr><td style={{ color: "#94a3b8", paddingRight: 12 }}>Account No.:</td><td style={{ fontFamily: "'Courier New', monospace" }}>50200005545807</td></tr>
                  <tr><td style={{ color: "#94a3b8", paddingRight: 12 }}>{isUsd ? "SWIFT:" : "IFSC:"}</td><td style={{ fontFamily: "'Courier New', monospace", fontWeight: isUsd ? 700 : 400, color: isUsd ? "#0066CC" : "#334155" }}>{isUsd ? "HDFCINBB" : "HDFC0000050"}</td></tr>
                  <tr><td style={{ color: "#94a3b8", paddingRight: 12 }}>{isUsd ? "IFSC (Domestic):" : "SWIFT (Intl):"}</td><td style={{ fontFamily: "'Courier New', monospace" }}>{isUsd ? "HDFC0000050" : "HDFCINBB"}</td></tr>
                  <tr><td style={{ color: "#94a3b8", paddingRight: 12 }}>Branch:</td><td>Dwarakanagar, Visakhapatnam, Andhra Pradesh, India</td></tr>
                  {isUsd && (
                    <tr><td style={{ color: "#94a3b8", paddingRight: 12 }}>Currency:</td><td style={{ fontWeight: 600, color: "#0066CC" }}>USD (US Dollar)</td></tr>
                  )}
                </tbody>
              </table>
            </td>
            <td style={{ width: "40%", verticalAlign: "bottom", textAlign: "right" }}>
              {/* Signature block — stamp overlays behind, ink signature overlays the line like a real signed doc */}
              <div style={{ position: "relative", display: "inline-block", minWidth: 220, height: 110, textAlign: "right" }}>
                {/* Round stamp (semi-transparent, offset left of signature) */}
                <img
                  src="https://customer-assets.emergentagent.com/job_cedee861-1d2e-4012-91c4-57a04e6b2415/artifacts/2x15byo8_Absolin_RoundSeal_Small.png"
                  alt="Company Seal"
                  crossOrigin="anonymous"
                  style={{
                    position: "absolute",
                    right: 130,
                    bottom: 8,
                    width: 92,
                    height: 92,
                    opacity: 0.72,
                    mixBlendMode: "multiply",
                    pointerEvents: "none",
                  }}
                />
                {/* Handwritten ink signature — sits above the signature line, slightly overlapping */}
                <img
                  src="https://customer-assets.emergentagent.com/job_cedee861-1d2e-4012-91c4-57a04e6b2415/artifacts/5ljhmc1p_Ramesh%20Sign.png"
                  alt="Signature"
                  crossOrigin="anonymous"
                  style={{
                    position: "absolute",
                    right: 34,
                    bottom: 30,
                    width: 130,
                    height: "auto",
                    mixBlendMode: "multiply",
                    pointerEvents: "none",
                  }}
                />
                {/* Signature line + caption */}
                <div style={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  borderTop: "1px solid #94a3b8",
                  padding: "6px 8px 0",
                  minWidth: 200,
                  textAlign: "center",
                }}>
                  <div style={{ fontSize: 10, color: "#334155", fontWeight: 600 }}>Authorised Signatory</div>
                  <div style={{ fontSize: 9, color: "#94a3b8" }}>For Absolin Software Solutions LLP</div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ================= FOOTER ================= */}
      <div style={{ marginTop: 28, paddingTop: 12, borderTop: "1px solid #e2e8f0", fontSize: 8, color: "#94a3b8", textAlign: "center", lineHeight: 1.6 }}>
        This is a system-generated statement of account and does not require a physical signature.<br />
        For any queries regarding this statement, please contact <span style={{ color: "#0066CC" }}>raghu@absolinsoft.com</span> quoting reference <strong>{statementNo}</strong> and your project name.
      </div>
    </div>
  );
}
